import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DrawerHeader } from '../../../../../components/Drawer/DrawerHeader';
import DataTable from '../../../../../components/DataTable/DataTable';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import InfoCardCompound from '../../../../../components/compound/InfoCardCompound';
import {
    useGetAptitudesQuery,
    useGetBatchEmployeeExamsQuery,
    useGetBatchEmployeeDocumentsQuery,
    useLoadBatchEmployeeResultsMutation
} from '../../../../../services/periodicosApi';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';
import { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import CircularProgress from '@mui/material/CircularProgress';
import { handleErrors } from '../../../../../utils/common';
import { BATCHES_TEST_IDS } from '../../../consts/testIds';

// TODO: set to true once the /periodicos/aptitudes endpoint is ready
const USE_APTITUDES_ENDPOINT = true;

const APTITUDES_MOCK = [
    { id: 1, nombre: 'Normal laboral' },
    { id: 2, nombre: 'Preexistente' },
    { id: 3, nombre: 'Desvío' },
    { id: 4, nombre: 'Enfermedad Profesional' },
    { id: 5, nombre: 'Estudio Faltante' },
    { id: 6, nombre: 'Práctica defectuosa' },
    { id: 7, nombre: 'Reconfirmar' }
];

const normalizeStatus = (value?: string) =>
    (value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .toUpperCase();

type ResultEdit = {
    idAptitud: number | null;
    observaciones: string;
};

const columnTitles = ({ t }: { t: TFunction }) => [
    '',
    t('drawers.medicalAudit.table.columnTitles.exam').toUpperCase(),
    t('drawers.medicalAudit.table.columnTitles.aptitude').toUpperCase(),
    t('drawers.medicalAudit.table.columnTitles.medicalObservations').toUpperCase()
];

const columnWidths = ['5%', '20%', '30%', '45%'];


const ObservacionesCell = memo(({ id, initialValue, onChange, testId, disabled }: {
    id: number;
    initialValue: string;
    onChange: (id: number, val: string) => void;
    testId: string;
    disabled?: boolean;
}) => {
    const [value, setValue] = useState(initialValue);
    return (
        <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Observaciones..."
            value={value}
            onChange={(e) => { setValue(e.target.value); onChange(id, e.target.value); }}
            inputProps={{ maxLength: 50, 'data-testid': testId }}
            sx={{ fontSize: 12 }}
            disabled={disabled}
        />
    );
});

interface PeriodicosMedicalAuditDrawerProps {
    selectedEmployee?: any;
    selectedBatchId?: number | null;
    selectedBatch?: any;
    handleCloseDrawer: () => void;
    onAfterSave?: () => void;
}

const PeriodicosMedicalAuditDrawer = ({
    selectedEmployee,
    selectedBatchId,
    selectedBatch,
    handleCloseDrawer,
    onAfterSave
}: PeriodicosMedicalAuditDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const dispatch = useAppDispatch();

    const selectedEmployeeId =
        Number(selectedEmployee?.idExamenEmpleado) ||
        Number(selectedEmployee?.examenEmpleado?.id) ||
        Number(selectedEmployee?.id) ||
        0;

    const { data: aptitudesFromApi = [] } = useGetAptitudesQuery(undefined, { skip: !USE_APTITUDES_ENDPOINT });
    const aptitudeOptions = USE_APTITUDES_ENDPOINT ? aptitudesFromApi : APTITUDES_MOCK;

    const { data: examenesData, isFetching: isFetchingExamenes } = useGetBatchEmployeeExamsQuery(
        selectedEmployee?.idEmpleadoLote,
        { skip: !selectedEmployee?.idEmpleadoLote }
    );

    const normalizedEstadoGestion = normalizeStatus(selectedEmployee?.estadoGestion);
    const isCitado = normalizedEstadoGestion === 'CITADO';
    const isPendingResults = normalizedEstadoGestion === 'PENDIENTE_RESULTADOS';
    const isPorAuditar = normalizedEstadoGestion === 'POR_AUDITAR';
    const isAuditadoParcial = normalizedEstadoGestion === 'AUDITADO_PARCIAL';
    const isAuditado = normalizedEstadoGestion === 'AUDITADO';
    const isLoteCerrado = normalizeStatus(selectedBatch?.estado || selectedBatch?.estadoLote) === 'CERRADO';
    const isReadOnly = isAuditado || isLoteCerrado;
    const canSave = !isReadOnly && (isPorAuditar || isAuditadoParcial);

    const { data: documentsData, isFetching: isFetchingDocumentos } = useGetBatchEmployeeDocumentsQuery(
        selectedEmployee?.idEmpleadoLote,
        { skip: !selectedEmployee?.idEmpleadoLote }
    );
    const [loadResults, { isLoading: isLoadingResults }] = useLoadBatchEmployeeResultsMutation();

    const aptitudeRows: any[] = examenesData || [];

    type DocumentItem = { idDocumento: number; url: string; nombreArchivo: string };

    const rawDocs = Array.isArray(documentsData?.data)
        ? documentsData.data
        : Array.isArray(documentsData)
        ? documentsData
        : [];

    const existingFiles: DocumentItem[] = rawDocs.map((item: any) =>
        typeof item === 'string'
            ? { idDocumento: 0, url: item, nombreArchivo: '' }
            : item
    );

    const [resultEdits, setResultEdits] = useState<Record<number, ResultEdit>>({});
    const [selectedExamIds, setSelectedExamIds] = useState<Set<number>>(new Set());
    const [massiveDialogOpen, setMassiveDialogOpen] = useState(false);
    const [partialAuditDialogOpen, setPartialAuditDialogOpen] = useState(false);
    const [massiveAptitudeId, setMassiveAptitudeId] = useState<number | null>(null);
    const [massiveObservaciones, setMassiveObservaciones] = useState('');
    const [massiveApplyVersion, setMassiveApplyVersion] = useState(0);
    const observacionesRef = useRef<Record<number, string>>({});
    const initializedRef = useRef(false);

    const allExamIds = aptitudeRows
        .map((row: any) => row?.id)
        .filter((id: any) => typeof id === 'number') as number[];
    const allSelected = allExamIds.length > 0 && allExamIds.every((id) => selectedExamIds.has(id));

    const handleToggleExam = (id: number) => {
        setSelectedExamIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleToggleAll = () => {
        if (allSelected) {
            setSelectedExamIds(new Set());
        } else {
            setSelectedExamIds(new Set(allExamIds));
        }
    };

    useEffect(() => {
        if (initializedRef.current || isFetchingExamenes || !aptitudeRows.length) return;
        const initial: Record<number, ResultEdit> = {};
        aptitudeRows.forEach((row: any) => {
            const id = row?.id;
            if (id) {
                initial[id] = {
                    idAptitud: row?.aptitud ?? null,
                    observaciones: row?.observaciones ?? ''
                };
                if (row?.observaciones) {
                    observacionesRef.current[id] = row.observaciones;
                }
            }
        });
        setResultEdits(initial);
        setMassiveApplyVersion((v) => v + 1);
        initializedRef.current = true;
    }, [aptitudeRows, isFetchingExamenes]);

    const rawTurno = selectedEmployee?.turnoAsignado || selectedEmployee?.fechaHoraTurno || null;
    const formattedTurno = rawTurno
        ? (dayjs(rawTurno).isValid() ? dayjs(rawTurno).format('DD/MM/YYYY - HH:mm') : rawTurno)
        : '-';

    const prestadorName =
        selectedBatch?.prestador?.razonSocial ||
        selectedBatch?.prestador?.nombre ||
        selectedEmployee?.prestador?.razonSocial ||
        selectedEmployee?.prestador?.nombre ||
        '-';

    const infoCardItems = [
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.establishment'),
            value: selectedEmployee?.establecimiento || selectedBatch?.establecimientoNombre || selectedBatch?.establecimiento?.nombre || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.sector'),
            value: selectedEmployee?.sector || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.workPosition'),
            value: selectedEmployee?.puesto || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.prestador'),
            value: prestadorName
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.appointment'),
            value: formattedTurno
        }
    ];

    const selectedEmployeeName =
        [selectedEmployee?.apellido, selectedEmployee?.nombre]
            .filter((part) => part && part !== '-')
            .join(' ') || '-';
    const selectedEmployeeCuil = selectedEmployee?.cuil || '-';

    const handleAptitudChange = (idExamenEmpleado: number, idAptitud: number | null) => {
        setResultEdits((prev) => ({
            ...prev,
            [idExamenEmpleado]: { ...prev[idExamenEmpleado], idAptitud }
        }));
    };

    const handleObservacionesChange = useCallback((idExamenEmpleado: number, value: string) => {
        observacionesRef.current[idExamenEmpleado] = value;
    }, []);

    const handleApplyMassiveAptitude = () => {
        if (!massiveAptitudeId) return;
        setResultEdits((prev) => {
            const next = { ...prev };
            selectedExamIds.forEach((id) => {
                next[id] = { ...next[id], idAptitud: massiveAptitudeId };
            });
            return next;
        });
        if (massiveObservaciones) {
            selectedExamIds.forEach((id) => {
                observacionesRef.current[id] = massiveObservaciones;
            });
            setMassiveApplyVersion((v) => v + 1);
        }
        setMassiveDialogOpen(false);
        setMassiveAptitudeId(null);
        setMassiveObservaciones('');
    };

    const handleViewDocument = (url: string) => {
        if (url) {
            window.open(url, '_blank');
        } else {
            enqueueSnackbar('No se pudo obtener el documento.', { variant: 'error' });
        }
    };

    const getFilenameFromUrl = (url: string): string => {
        try {
            const pathname = new URL(url).pathname;
            return decodeURIComponent(pathname.split('/').pop() || 'Archivo');
        } catch {
            return url.split('/').pop()?.split('?')[0] || 'Archivo';
        }
    };

    const doSave = async () => {
        const resultados = Object.entries(resultEdits)
            .filter(([, edit]) => edit.idAptitud !== null)
            .map(([id, edit]) => ({
                idExamenEmpleado: Number(id),
                idAptitud: edit.idAptitud!,
                ...(observacionesRef.current[Number(id)] ? { observaciones: observacionesRef.current[Number(id)] } : {})
            }));

        try {
            const res: any = await loadResults({ idEmpleado: selectedEmployee?.idEmpleadoLote, resultados });
            if (res?.error) {
                handleErrors(res.error);
                return;
            }
            enqueueSnackbar('Auditoría guardada correctamente.', { variant: 'success' });
            onAfterSave?.();
            handleCloseDrawer();
        } catch {
            dispatch(
                showAlert({
                    title: 'No se pudo guardar la auditoría médica',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleSave = () => {
        const hasIncomplete = aptitudeRows.some((row: any) => !resultEdits[row?.id]?.idAptitud);
        if (hasIncomplete) {
            setPartialAuditDialogOpen(true);
            return;
        }
        doSave();
    };

    const handleConfirmPartialSave = () => {
        setPartialAuditDialogOpen(false);
        doSave();
    };

    const isSaving = isLoadingResults;

    const parentTheme = useTheme();
    const scaledTheme = useMemo(() => createTheme(parentTheme, {
        typography: {
            body1: { fontSize: '0.7rem' },
            body2: { fontSize: '0.62rem' },
            subtitle1: { fontSize: '0.95rem' },
            subtitle2: { fontSize: '0.65rem' },
            caption: { fontSize: '0.72rem' },
        },
        components: {
            MuiButton: { styleOverrides: { root: { fontSize: '0.7rem' } } },
            MuiInputBase: { styleOverrides: { root: { fontSize: '0.72rem' } } },
            MuiMenuItem: { styleOverrides: { root: { fontSize: '0.72rem' } } },
        }
    }), [parentTheme]);

    if (isCitado) {
        return (
            <ThemeProvider theme={scaledTheme}>
                <Box display="flex" flexDirection="column" sx={{ height: '100%' }}>
                    <DrawerHeader onIconClick={handleCloseDrawer} text={t('drawers.medicalAudit.title')} />
                    <Box sx={{ p: 3, flex: 1 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                            Se podrán cargar resultados una vez que se confirme la asistencia del empleado.
                        </Typography>
                        {isFetchingExamenes ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                {aptitudeRows.map((row: any, idx: number) => (
                                    <Box component="li" key={row?.id ?? idx} sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.primary">
                                            {row?.examen?.descripcion || `Examen ${row?.id ?? idx + 1}`}
                                        </Typography>
                                    </Box>
                                ))}
                                {aptitudeRows.length === 0 && (
                                    <Typography variant="body2" color="text.disabled">
                                        Sin exámenes definidos
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                    <BackdropComponent loading={isFetchingExamenes} />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={scaledTheme}>
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            sx={{ height: '100%' }}
        >
            <Box sx={{ overflow: 'auto', pb: 2 }}>
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={isReadOnly ? 'Auditoría Médica — Visualización' : t('drawers.medicalAudit.title')}
                />
                <InfoCardCompound columns={5}>
                    <InfoCardCompound.Avatar />
                    <InfoCardCompound.Title subtitle={`CUIL: ${selectedEmployeeCuil}`}>
                        {selectedEmployeeName}
                    </InfoCardCompound.Title>
                    {infoCardItems.map((item) => (
                        <InfoCardCompound.Item key={item.label} label={item.label}>
                            {item.value}
                        </InfoCardCompound.Item>
                    ))}
                </InfoCardCompound>

                {/* Documentación de Resultados */}
                <Box sx={{ mt: 2 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <CardHeader
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                bgcolor: 'grey.50',
                                borderBottom: 1,
                                borderColor: 'grey.100'
                            }}
                            title={
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    color="text.primary"
                                    lineHeight={1.2}
                                >
                                    Documentación de Resultados
                                </Typography>
                            }
                        />
                        <CardContent sx={{ p: 2.5 }}>
                            <Grid container spacing={2}>
                                {existingFiles.map((doc, index) => (
                                    <Grid item xs={12} key={doc.idDocumento || index}>
                                        <Card
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                px: 2,
                                                py: 1.25,
                                                bgcolor: 'background.paper',
                                                border: 1.5,
                                                borderColor: 'grey.300',
                                                borderRadius: 2,
                                                '&:hover': { boxShadow: 2 }
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                fontWeight={500}
                                                color="text.primary"
                                                noWrap
                                                sx={{ flex: 1, mr: 1 }}
                                            >
                                                {doc.nombreArchivo || getFilenameFromUrl(doc.url)}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                sx={{ '&:hover': { bgcolor: 'grey.100' }, flexShrink: 0 }}
                                                title="Ver archivo"
                                                onClick={() => handleViewDocument(doc.url)}
                                                data-testid={`${BATCHES_TEST_IDS.drawers.medicalAudit.existingFileViewPrefix}${index}`}
                                            >
                                                <VisibilityOutlinedIcon
                                                    sx={{ fontSize: 18, color: 'text.secondary' }}
                                                />
                                            </IconButton>
                                        </Card>
                                    </Grid>
                                ))}

                                {existingFiles.length === 0 && (
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            color="text.disabled"
                                            textAlign="center"
                                            sx={{ py: 2 }}
                                        >
                                            Sin documentos cargados
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Box>

                {/* Detalle de Aptitud por Examen */}
                {!isPendingResults && (<Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                            Detalle de Aptitud por Examen
                        </Typography>
                        {!isReadOnly && selectedExamIds.size > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2, px: 1.5, py: 0.75 }}>
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1d4ed8' }}>
                                    {`${selectedExamIds.size} examen${selectedExamIds.size !== 1 ? 'es' : ''} seleccionado${selectedExamIds.size !== 1 ? 's' : ''}`}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setMassiveDialogOpen(true)}
                                    sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5, px: 1.25 }}
                                >
                                    Asignar aptitud masiva
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <DataTable
                                columnTitles={[
                                    {
                                        label: '',
                                        showSelectAllCheck: true,
                                        checked: allSelected,
                                        onSelectAllCheckPress: handleToggleAll
                                    },
                                    ...columnTitles({ t }).slice(1)
                                ]}
                                columnWidths={columnWidths}
                                items={{ data: { data: aptitudeRows } }}
                                filters={{ skip: 0, take: 1000 }}
                                onSelectRow={() => {}}
                                selectedItemFromAction={null}
                                menuOpen={false}
                                isFetching={isFetchingExamenes}
                                noPaginated={true}
                                headers={[
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => {
                                            const id = item?.id;
                                            if (!id || isReadOnly) return <Checkbox disabled />;
                                            return (
                                                <Checkbox
                                                    checked={selectedExamIds.has(id)}
                                                    onChange={() => handleToggleExam(id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            );
                                        }
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            item?.examen?.descripcion || '-'
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => {
                                            const id = item?.id;
                                            if (!id) return '-';
                                            const hasValue = !!resultEdits[id]?.idAptitud;
                                            return (
                                                <Box sx={{ position: 'relative' }}>
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        displayEmpty
                                                        disabled={isReadOnly}
                                                        value={resultEdits[id]?.idAptitud ?? ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            handleAptitudChange(id, val === '' ? null : Number(val));
                                                        }}
                                                        inputProps={{ 'data-testid': `${BATCHES_TEST_IDS.drawers.medicalAudit.examAptitudeSelectPrefix}${id}` }}
                                                        sx={{ fontSize: 12, '& .MuiSelect-select': { pr: hasValue ? '48px' : undefined } }}
                                                    >
                                                        <MenuItem value="">
                                                            <Typography variant="caption" color="text.disabled">
                                                                Seleccionar...
                                                            </Typography>
                                                        </MenuItem>
                                                        {aptitudeOptions.map((aptitud) => (
                                                            <MenuItem key={aptitud.id} value={aptitud.id}>
                                                                {aptitud.nombre}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {hasValue && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAptitudChange(id, null);
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                right: 28,
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                p: 0.25,
                                                                color: 'text.disabled',
                                                                '&:hover': { color: 'text.primary', bgcolor: 'transparent' }
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            );
                                        }
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => {
                                            const id = item?.id;
                                            if (!id) return '-';
                                            return (
                                                <ObservacionesCell
                                                    key={`obs-${id}-${massiveApplyVersion}`}
                                                    id={id}
                                                    initialValue={observacionesRef.current[id] ?? ''}
                                                    onChange={handleObservacionesChange}
                                                    testId={`${BATCHES_TEST_IDS.drawers.medicalAudit.examObservationsPrefix}${id}`}
                                                    disabled={isReadOnly}
                                                />
                                            );
                                        }
                                    }
                                ]}
                            />
                        </Box>
                    </Card>
                </Box>)}

            </Box>

            <Dialog
                open={partialAuditDialogOpen}
                onClose={() => setPartialAuditDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 700, pb: 1 }}>
                    {t('drawers.medicalAudit.partialAuditDialog.title')}
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body1" color="text.secondary">
                        {t('drawers.medicalAudit.partialAuditDialog.body')}{' '}
                        <Typography component="span" variant="body1" fontWeight={700} color="text.primary">
                            {t('drawers.medicalAudit.partialAuditDialog.statusHighlight')}
                        </Typography>
                        {t('drawers.medicalAudit.partialAuditDialog.bodySuffix')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                    <Button onClick={() => setPartialAuditDialogOpen(false)}>
                        {t('drawers.medicalAudit.partialAuditDialog.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmPartialSave}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('drawers.medicalAudit.partialAuditDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={massiveDialogOpen}
                onClose={() => { setMassiveDialogOpen(false); setMassiveAptitudeId(null); setMassiveObservaciones(''); }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, pb: 1 }}>
                    Asignar aptitud masiva
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
                        {`${selectedExamIds.size} examen${selectedExamIds.size !== 1 ? 'es' : ''} seleccionado${selectedExamIds.size !== 1 ? 's' : ''}`}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Aptitud *
                        </Typography>
                        <Select
                            size="small"
                            fullWidth
                            displayEmpty
                            value={massiveAptitudeId ?? ''}
                            onChange={(e) => setMassiveAptitudeId(e.target.value === '' ? null : Number(e.target.value))}
                            sx={{ mt: 0.5, fontSize: 13 }}
                        >
                            <MenuItem value="" disabled>
                                <Typography variant="caption" color="text.disabled">Seleccionar...</Typography>
                            </MenuItem>
                            {aptitudeOptions.map((apt) => (
                                <MenuItem key={apt.id} value={apt.id}>{apt.nombre}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Observaciones <Typography component="span" variant="caption" color="text.disabled">(opcional)</Typography>
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Observaciones..."
                            value={massiveObservaciones}
                            onChange={(e) => setMassiveObservaciones(e.target.value)}
                            inputProps={{ maxLength: 50 }}
                            sx={{ mt: 0.5, fontSize: 13 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                    <Button onClick={() => { setMassiveDialogOpen(false); setMassiveAptitudeId(null); setMassiveObservaciones(''); }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApplyMassiveAptitude}
                        disabled={!massiveAptitudeId}
                        sx={{ textTransform: 'none' }}
                    >
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>

            <BackdropComponent loading={isFetchingExamenes || isFetchingDocumentos || isSaving} />

            {!isReadOnly && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseDrawer}
                        disabled={isSaving}
                        data-testid={BATCHES_TEST_IDS.drawers.medicalAudit.cancelButton}
                    >
                        {t('drawer.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={isSaving || !canSave}
                        data-testid={BATCHES_TEST_IDS.drawers.medicalAudit.saveButton}
                        sx={{ textTransform: 'none', minWidth: 160 }}
                    >
                        Guardar auditoría
                    </Button>
                </Box>
            )}
        </Box>
        </ThemeProvider>
    );
};

export { PeriodicosMedicalAuditDrawer };
